import {
    ArrowRight,
    BadgeCheck,
    Check,
    Headphones,
    Lock,
    Shield,
} from 'lucide-react';

import { Link } from 'react-router-dom';

const features = [
    {
        icon: Check,
        text: 'Suivi documentaire et traçabilité de vos démarches',
    },
    {
        icon: Check,
        text: 'Alertes et rappels sur vos échéances légales et administratives',
    },
    {
        icon: Check,
        text: 'Accompagnement juridique et comptable par des experts qualifiés',
    },
    {
        icon: Check,
        text: 'Sécurité des données et accès strictement contrôlés',
    },
];

const trustItems = [
    {
        icon: Shield,
        title: 'Hébergement sécurisé',
    },
    {
        icon: BadgeCheck,
        title: 'Données chiffrées',
    },
    {
        icon: Lock,
        title: 'Accompagnement personnalisé',
    },
    {
        icon: Headphones,
        title: 'Support dédié',
    },
];

export function HeroSection() {
    return (
        <section className="relative isolate overflow-hidden bg-[#FAFAFB] text-[#1A1D23]">
            {/* Background image */}
            <div
                className="pointer-events-none absolute inset-0 z-0 bg-cover bg-center"
                style={{
                    backgroundImage: "url('/images/swiss-bg.png')",
                }}
            />

            {/* Voile clair */}
            <div className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-b from-[#FAFAFB]/14 via-transparent to-[#FAFAFB]/20" />

            {/* Halo rouge */}
            <div
                className="pointer-events-none absolute inset-0 z-0"
                style={{
                    background:
                        'radial-gradient(ellipse 55% 45% at 85% 10%, rgba(235,0,0,0.05) 0%, transparent 65%)',
                }}
            />

            {/* Grain */}
            <div
                className="pointer-events-none absolute inset-0 z-0 opacity-[0.03] mix-blend-multiply"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                }}
            />

            {/* Vignette */}
            <div
                className="pointer-events-none absolute inset-0 z-0"
                style={{
                    boxShadow:
                        'inset 0 0 100px rgba(26,29,35,0.05)',
                }}
            />

            <div className="relative z-10 mx-auto grid min-h-[calc(100vh-120px)] max-w-[1200px] grid-cols-1 items-center gap-14 px-7 py-12 lg:grid-cols-[0.85fr_1.15fr] lg:py-16">

                {/* COLONNE GAUCHE */}
                <div className="max-w-xl rounded-3xl bg-white/15 p-8 backdrop-blur-md lg:p-10">

                    {/* Badge */}
                    <div className="mb-5 inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-[#b20303] backdrop-blur-sm">
                        Plateforme B2B intégrée
                    </div>

                    {/* Titre */}
                    <h1
                        className="mb-6 text-[29px] font-bold leading-tight tracking-[-0.03em] text-[#1A1D23] lg:text-[39px]"
                        style={{
                            textShadow:
                                '0 2px 16px rgba(255,255,255,0.6)',
                        }}
                    >
                        Protégez votre entreprise et validez vos documents
                    </h1>

                    {/* Description */}
                    <p
                        className="mb-7 max-w-lg text-[16px] leading-7 text-[#2E323B]"
                        style={{
                            textShadow:
                                '0 1px 10px rgba(255,255,255,0.55)',
                        }}
                    >
                        Un accompagnement pensé pour les besoins réels des PME
                        suisses : centralisez vos démarches administratives,
                        sécurisez vos documents officiels et suivez vos
                        obligations légales, avec le soutien d&apos;une équipe
                        d&apos;experts.
                    </p>

                    {/* Fonctionnalités */}
                    <ul className="mb-8 flex flex-col gap-3">
                        {features.map(({ icon: Icon, text }) => (
                            <li
                                key={text}
                                className="flex items-start gap-3 text-[14.5px] font-medium text-[#1A1D23]"
                            >
                                <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-white/80 text-[#b20303] shadow-sm">
                                    <Icon className="h-3 w-3" />
                                </span>

                                <span
                                    style={{
                                        textShadow:
                                            '0 1px 8px rgba(255,255,255,0.6)',
                                    }}
                                >
                                    {text}
                                </span>
                            </li>
                        ))}
                    </ul>

                    {/* CTA */}
                    <Link
                        to="/nos-services"
                        className="group relative inline-flex items-center gap-3 overflow-hidden rounded-xl px-8 py-4 text-[15px] font-semibold text-white transition-all duration-500 hover:-translate-y-1"
                        style={{
                            background:
                                'linear-gradient(135deg, #b20303, #8f0202)',
                            boxShadow:
                                '0 8px 25px rgba(178,3,3,0.35), 0 2px 6px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.25)',
                        }}
                    >
                        {/* Reflet animé */}
                        <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 group-hover:translate-x-full" />

                        {/* Bordure */}
                        <div className="pointer-events-none absolute inset-0 rounded-xl border border-white/20" />

                        {/* Contenu */}
                        <span className="relative z-10 flex items-center gap-3 tracking-wide">
                            Découvrez notre plateforme

                            <ArrowRight
                                className="h-4 w-4 transition-all duration-300 group-hover:translate-x-1"
                                strokeWidth={2.5}
                            />
                        </span>
                    </Link>

                    {/* Éléments de confiance */}
                    <div className="mt-8 overflow-hidden rounded-lg border border-white/40 bg-white/70 shadow-[0_1px_3px_rgba(15,23,42,0.04)] backdrop-blur-sm">
                        <div className="grid grid-cols-2 md:grid-cols-4">
                            {trustItems.map(
                                ({ icon: Icon, title }, index) => (
                                    <div
                                        key={title}
                                        className={`flex flex-col items-center justify-center gap-3 px-5 py-5 text-center ${
                                            index !== trustItems.length - 1
                                                ? 'border-r border-white/40 md:last:border-r-0'
                                                : ''
                                        }`}
                                    >
                                        <Icon className="h-8 w-8 text-[#6E7480]" />

                                        <span className="text-[13px] font-medium leading-snug text-[#5B616E]">
                                            {title}
                                        </span>
                                    </div>
                                ),
                            )}
                        </div>
                    </div>
                </div>

                {/* COLONNE DROITE */}
                <div className="relative">
                    {/* Halo derrière l'image */}
                    <div className="absolute -inset-6 rounded-[32px] bg-[#E9EAED] blur-2xl" />

                    {/* Image */}
                    <div className="relative overflow-hidden rounded-xl border border-[#E4E6EA] bg-white shadow-[0_20px_50px_rgba(15,23,42,0.08)]">
                        <img
                            src="/images/background-helvedoc-1.jpeg"
                            alt="Présentation professionnelle"
                            className="h-auto max-h-[660px] w-full object-contain"
                        />

                        <div className="absolute inset-0 bg-gradient-to-t from-white/10 via-transparent to-transparent" />
                    </div>
                </div>
            </div>
        </section>
    );
}

export default HeroSection;